import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Typography, Box, makeStyles,
} from '@material-ui/core';
import MyForm from '../../form/MyForm';
import pageUtilsPropTypes from '../../page/page-prop-types';
import { AppState } from '../../state/MyStateProvider';
import formFieldsPropTypes from '../../form/form-prop-types';
import prepareFormikInitialValues from '../../form/form-helpers';
import seedLogo from '../../images/logo.svg';
import { seedAppIcons } from '../../icon/seed-icons';
import AuthPage from '../../page/AuthPage';
import FirebaseRepository from '../FirebaseRepository';
import { FirebaseState } from '../MyFirebaseProvider';
import { firebaseEmailLinkFields, firebaseEmailLinkValidationSchema } from './firebase-forms';

const useStyles = makeStyles((theme) => ({
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: 200,
    height: 'auto',
  },
}));

const SeedFirebaseLoginLinkPage = (props) => {
  const {
    fields,
    validationSchema,
    pageUtils,
    logo,
    applicationName,
    icons,
  } = props;
  const classes = useStyles();
  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);

  const [isLoading, setIsLoading] = React.useState(false);
  const [firebaseRepo] = React.useState(new FirebaseRepository(state, dispatch, firebaseConfig));

  React.useEffect(() => {
    if (firebaseRepo.firebaseApi.firebase.auth().isSignInWithEmailLink(window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        // User opened the link on a different device. To prevent session fixation
        // attacks, ask the user to provide the associated email again. For example:
        email = window.prompt('Please provide your email for confirmation');
      }

      setIsLoading(true);
      pageUtils.loadSomethingSilent(
        () => firebaseRepo.loginEmailLink({ email, link: window.location.href }),
        (payload) => {
          firebaseRepo.saveUser(payload.user);
        },
        (error) => {
          if (error.message) {
            pageUtils.showSnackbar(error.message, 'error');
          } else {
            pageUtils.showSnackbar(error, 'error');
          }
        },
        () => {
          setIsLoading(false);
        },
      );
    }

    return () => {

    };
  }, [firebaseRepo, pageUtils]);

  const handleSubmitLogin = (values) => {
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.registerEmailLink(values),
      ({ email }) => {
        window.localStorage.setItem('emailForSignIn', email);
        pageUtils.showSnackbar(`We have sent an email to ${email}`, 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(error, 'error');
        }
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  const initialValues = prepareFormikInitialValues(null, true, fields);

  return (
    <AuthPage isLoading={isLoading} logo={logo}>
      <div className={classes.logoContainer}>
        <img className={classes.logo} alt={`${applicationName} Logo`} src={logo} />
      </div>
      <Typography align="center" component="h1" variant="h5">
        <FormattedMessage id="seed.firebase.authentication.linkEmail.heading" values={{ applicationName }} />
      </Typography>
      <Box mt={6} />
      <MyForm
        fields={fields}
        validationSchema={validationSchema}
        onSubmit={handleSubmitLogin}
        disabled={isLoading}
        submitButtonLabelId="seed.firebase.authentication.linkEmail.button.submit"
        initialValues={initialValues}
        icons={icons}
      />
    </AuthPage>
  );
};

SeedFirebaseLoginLinkPage.propTypes = {
  fields: PropTypes.arrayOf(formFieldsPropTypes),
  validationSchema: PropTypes.object,
  pageUtils: pageUtilsPropTypes.isRequired,
  logo: PropTypes.string,
  applicationName: PropTypes.string,
  icons: PropTypes.objectOf(PropTypes.object),
};

SeedFirebaseLoginLinkPage.defaultProps = {
  fields: firebaseEmailLinkFields,
  validationSchema: firebaseEmailLinkValidationSchema,
  logo: seedLogo,
  applicationName: 'react-seed',
  icons: seedAppIcons,
};

export default SeedFirebaseLoginLinkPage;
